import { Check } from "lucide-react";
import { ContextMenu as M } from "radix-ui";
import type { ReactNode } from "react";

import { cx } from "@/lib/cx";

const content = "animate-pop-in z-50 min-w-48 rounded-md border border-line bg-surface-raised p-1 shadow-md";
const item = cx(
  "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none",
  "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-surface-hover",
);

/**
 * A right-click (or long-press) menu over an area, styled like Menu. The area decides what the
 * menu holds when it opens (`onOpenChange` or its own contextmenu handler), so one menu serves
 * every element of a canvas.
 */
export function ContextMenu({ children, menu, label, onOpenChange }: { children: ReactNode; menu: ReactNode; label: string; onOpenChange?: (open: boolean) => void }) {
  return (
    <M.Root onOpenChange={onOpenChange} modal={false}>
      <M.Trigger asChild>{children}</M.Trigger>
      <M.Portal>
        <M.Content aria-label={label} className={content} collisionPadding={8}>
          {menu}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

export function ContextItem({ children, onSelect, icon, destructive, disabled, shortcut, checked }: {
  children: ReactNode;
  onSelect?: () => void;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  shortcut?: string;
  /** A choice among others (shown with a dot when it is the current one). */
  checked?: boolean;
}) {
  return (
    <M.Item disabled={disabled} onSelect={onSelect} className={cx(item, destructive ? "text-danger-text" : "text-fg")} aria-checked={checked} role={checked === undefined ? "menuitem" : "menuitemradio"}>
      <span className="flex w-4 justify-center text-fg-subtle [&>svg]:size-4">
        {checked !== undefined ? checked && <span className="size-1.5 rounded-full bg-accent" /> : icon}
      </span>
      <span className="flex-1">{children}</span>
      {shortcut && <kbd className="font-sans text-xs text-fg-subtle">{shortcut}</kbd>}
    </M.Item>
  );
}

/** An on/off choice (a flag): ticked when on. */
export function ContextCheckItem({ children, checked, onSelect, disabled }: { children: ReactNode; checked: boolean; onSelect?: () => void; disabled?: boolean }) {
  return (
    <M.CheckboxItem checked={checked} disabled={disabled} onSelect={onSelect} className={cx(item, "text-fg")}>
      <span className="flex w-4 justify-center text-fg-subtle [&>svg]:size-4">
        <M.ItemIndicator>
          <Check aria-hidden className="text-success-text" />
        </M.ItemIndicator>
      </span>
      <span className="flex-1">{children}</span>
    </M.CheckboxItem>
  );
}

export function ContextSub({ label, icon, children, disabled }: { label: ReactNode; icon?: ReactNode; children: ReactNode; disabled?: boolean }) {
  return (
    <M.Sub>
      <M.SubTrigger disabled={disabled} className={cx(item, "text-fg data-[state=open]:bg-surface-hover")}>
        <span className="flex w-4 justify-center text-fg-subtle [&>svg]:size-4">{icon}</span>
        <span className="flex-1">{label}</span>
        <span aria-hidden className="text-fg-subtle">›</span>
      </M.SubTrigger>
      <M.Portal>
        <M.SubContent className={content} sideOffset={4} collisionPadding={8}>
          {children}
        </M.SubContent>
      </M.Portal>
    </M.Sub>
  );
}

export function ContextLabel({ children }: { children: ReactNode }) {
  return <M.Label className="px-2 pt-1.5 pb-1 text-xs font-medium text-fg-subtle">{children}</M.Label>;
}

export function ContextSeparator() {
  return <M.Separator className="my-1 h-px bg-line" />;
}
